"use client";

/**
 * `useShippoBetaAccess` — client behaviour hook that fetches the authed
 * seller's enrolment in the Shippo live-rate beta. One-shot, mount-only
 * fetch; result is cached in module state so multiple consumers on the
 * same page share a single network call.
 *
 * Fail-closed semantics: while loading, while unauthed, on network error,
 * or on the API returning false → `hasBetaAccess: false`. This means
 * containers reading the hook get the conservative answer until the truth
 * arrives, never the optimistic one. A flicker from "unsupported" →
 * "limited" on first paint is acceptable; the inverse (briefly promising
 * live-rate then yanking it) is not.
 *
 * `useEffect` is used here for syncing with the outside world (the API),
 * which is one of the legitimate uses per `react-state-design.md`. The
 * hook is the encapsulation that callers expect — they never see a raw
 * `useEffect(() => fetch(...))`.
 *
 * Module-level cache (`cachedResult`) is intentionally not a Suspense
 * resource — Suspense for one boolean would be overkill, and the
 * fail-closed default makes the loading window safe.
 *
 * @see app/api/me/shippo-beta-access/route.ts — server endpoint
 * @see lib/shipping/country-access.ts — `getEffectiveTier` consumer
 */

import { useEffect, useState } from "react";
import type { ShippoBetaAccessResponse } from "@/app/api/me/shippo-beta-access/route";

let cachedResult: boolean | null = null;
let inFlight: Promise<boolean> | null = null;

async function fetchOnce(): Promise<boolean> {
  if (cachedResult !== null) return cachedResult;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const res = await fetch("/api/me/shippo-beta-access", {
        method: "GET",
        credentials: "same-origin",
        headers: { accept: "application/json" },
      });
      if (!res.ok) {
        cachedResult = false;
        return false;
      }
      const body = (await res.json()) as ShippoBetaAccessResponse;
      cachedResult = Boolean(body.hasBetaAccess);
      return cachedResult;
    } catch {
      cachedResult = false;
      return false;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

export interface UseShippoBetaAccessResult {
  /** Fail-closed: `false` while loading / on error / when not enrolled. */
  hasBetaAccess: boolean;
  /** `true` until the first response arrives. */
  loading: boolean;
}

export function useShippoBetaAccess(): UseShippoBetaAccessResult {
  const [hasBetaAccess, setHasBetaAccess] = useState<boolean>(
    cachedResult ?? false,
  );
  const [loading, setLoading] = useState<boolean>(cachedResult === null);

  useEffect(() => {
    if (cachedResult !== null) {
      setHasBetaAccess(cachedResult);
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchOnce().then((result) => {
      if (cancelled) return;
      setHasBetaAccess(result);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { hasBetaAccess, loading };
}

/** Test-only: reset the module-level cache. */
export function __resetShippoBetaAccessCacheForTests(): void {
  cachedResult = null;
  inFlight = null;
}
